import React, {
  createContext,
  useState,
  ReactNode,
  useRef,
  useContext
} from 'react'
import LoadingBackdrop from 'components/LoadingBackdrop'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { useTranslation } from 'react-i18next'

interface LoadingContextType {
  showLoading: (timeoutInMs?: number) => void
  stopLoading: () => void
}

export const LoadingContext = createContext<LoadingContextType>({
  showLoading: () => undefined,
  stopLoading: () => undefined
})

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { showMessage } = useContext(SnackbarContext)

  const showLoading = (timeoutInMs = 40000) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setIsLoading(true)

    timeoutRef.current = setTimeout(() => {
      setIsLoading(false)
      timeoutRef.current = null
      showMessage(t('snackbar.general.networkError'), 'error')
    }, timeoutInMs)
  }

  const stopLoading = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsLoading(false)
  }

  return (
    <LoadingContext.Provider value={{ showLoading, stopLoading }}>
      {children}
      <LoadingBackdrop isOpen={isLoading} />
    </LoadingContext.Provider>
  )
}
